/**
 * Frontend ConditionGroup evaluator — recursive AND/OR.
 *
 * VERBATIM parity port of the canonical server evaluators:
 *   - main-consumer `src/lib/workflow/condition/evaluator.ts`
 *   - main-backend  `org/nikita/workflow/condition/ConditionEvaluator.java`
 *
 * Used FE-side for live builder previews (email/document node pruning,
 * wallet/calendar field gating). The server paths re-evaluate the same
 * ConditionGroup at send/render time, so this MUST stay byte-for-byte
 * aligned with those two — do not "improve" the operator semantics here
 * without changing all three. See plan:
 * .claude/plans/builder-conditional-rendering.md
 *
 * Empty / null group → returns `true` (fire-on-no-conditions semantics).
 *
 * 2026-09-23 (WB audit W5h): three parity gaps closed against the shared
 * fixture `src/lib/__fixtures__/condition-eval-parity.json` — an empty
 * SUB-group gets no vote (`groupHasFilters`), authored operands are
 * edge-trimmed at the one seam where they become comparable, and a zero-millis
 * ISO instant equals its `Z` spelling under `=`/`!=`/`in`/`not_in`.
 */
import { ConditionGroup, ConditionFilter } from "@/types/EventRule";
import { normalizeFieldValue } from "@/lib/utils/input-validator";

/**
 * Does this group carry a filter anywhere beneath it?
 *
 * Port of the Worker's `groupHasFilters` (website-builder-backend
 * `workflow/condition/evaluator.ts`). A group whose only content is more
 * empty groups is not a rule, however deep it nests; `null`, `undefined` and
 * non-objects are not rules either.
 */
export function groupHasFilters(conds: unknown): boolean {
  if (conds === null || conds === undefined || typeof conds !== "object") return false;
  const filters = (conds as { filters?: unknown[] }).filters;
  if (Array.isArray(filters) && filters.length > 0) return true;
  const groups = (conds as { groups?: unknown[] }).groups;
  if (!Array.isArray(groups)) return false;
  return groups.some((g) => groupHasFilters(g));
}

export function evaluateConditionGroup(
  conditionGroup: ConditionGroup | null | undefined,
  data: Record<string, unknown> | null | undefined,
  previousData?: Record<string, unknown> | null,
): boolean {
  if (
    conditionGroup === null ||
    conditionGroup === undefined ||
    Object.keys(conditionGroup).length === 0
  ) {
    return true;
  }

  const operator = conditionGroup.operator ?? "AND";
  const filters = conditionGroup.filters;
  const groups = conditionGroup.groups;

  let hasAny = false;

  if (Array.isArray(filters)) {
    for (const filter of filters) {
      hasAny = true;
      const result = evaluateFilter(filter, data, previousData ?? null);
      if (operator === "OR" && result) return true;
      if (operator === "AND" && !result) return false;
    }
  }

  if (Array.isArray(groups)) {
    for (const subgroup of groups) {
      // A sub-group carrying no filter ANYWHERE beneath it is not a rule, so it
      // must not get a vote. It used to: the fire-on-no-conditions arm above
      // answers `true` for `{}`, and under OR a single `true` returns at once —
      // so an empty group BESIDE a real one turned "vip only" into "everyone"
      // in the cockpit preview and match counter, while the Worker (since
      // 2026-08-14) and Java said "vip only". `sanitizeConditionGroup` prunes
      // such groups on SAVE only; the preview evaluates the live tree.
      if (!groupHasFilters(subgroup)) continue;
      hasAny = true;
      const result = evaluateConditionGroup(subgroup, data, previousData ?? null);
      if (operator === "OR" && result) return true;
      if (operator === "AND" && !result) return false;
    }
  }

  if (!hasAny) return true;
  return operator === "AND";
}

function evaluateFilter(
  filter: ConditionFilter,
  data: Record<string, unknown> | null | undefined,
  previousData: Record<string, unknown> | null,
): boolean {
  const key = filter.key;
  const op = filter.operator;
  const filterValue = filter.value;

  if (key === null || key === undefined || op === null || op === undefined) {
    return false;
  }

  const actualValue = resolveValue(key, data);
  const actual =
    actualValue !== null && actualValue !== undefined ? String(actualValue) : "";
  // THE OPERAND, normalized at the one point where it becomes comparable —
  // the same seam Java uses (`ConditionEvaluator` ⇒ `FieldValueNormalizer`)
  // and the same function the cockpit already applies to submitted values.
  // Only the operand moves; the stored side stays raw.
  const expected =
    filterValue !== null && filterValue !== undefined
      ? Array.isArray(filterValue)
        ? ""
        : normalizeFieldValue(String(filterValue))
      : "";

  switch (op) {
    case "=":
      return canonicalIso(actual) === canonicalIso(expected);
    case "!=":
      return canonicalIso(actual) !== canonicalIso(expected);
    case "contains":
      if (Array.isArray(actualValue)) {
        return (actualValue as unknown[]).map(String).includes(expected);
      }
      // "contains" is a question about TEXT. `actual` is String(actualValue), so
      // without this guard a boolean/number/date was rendered and then searched
      // — TRUE here where the server's Mongo twin ($regex) says FALSE, and this
      // file's header demands byte-parity with it.
      if (!isTextual(actualValue)) return false;
      return actual.toLowerCase().includes(expected.toLowerCase());
    case "not_contains":
      if (Array.isArray(actualValue)) {
        return !(actualValue as unknown[]).map(String).includes(expected);
      }
      // Mongo's negated regex on a non-string field matches every document.
      if (!isTextual(actualValue)) return true;
      return !actual.toLowerCase().includes(expected.toLowerCase());
    case "is_empty":
      return (
        actualValue === null ||
        actualValue === undefined ||
        actual.length === 0 ||
        (Array.isArray(actualValue) && (actualValue as unknown[]).length === 0)
      );
    case "is_not_empty":
      return (
        actualValue !== null &&
        actualValue !== undefined &&
        actual.length !== 0 &&
        !(Array.isArray(actualValue) && (actualValue as unknown[]).length === 0)
      );
    // An ordered comparison against a LIST is not a question with an answer;
    // this string-compared String(array), i.e. the literal "A,B".
    case ">":
      if (Array.isArray(actualValue)) return false;
      return compareValues(actual, expected) > 0;
    case "<":
      if (Array.isArray(actualValue)) return false;
      return compareValues(actual, expected) < 0;
    case ">=":
      if (Array.isArray(actualValue)) return false;
      return compareValues(actual, expected) >= 0;
    case "<=":
      if (Array.isArray(actualValue)) return false;
      return compareValues(actual, expected) <= 0;
    case "changed_to": {
      if (previousData === null) return false;
      const oldRaw = resolveValue(key, previousData);
      const oldVal = oldRaw !== null && oldRaw !== undefined ? String(oldRaw) : "";
      return oldVal !== expected && actual === expected;
    }
    case "changed_from": {
      if (previousData === null) return false;
      const oldRaw = resolveValue(key, previousData);
      const oldVal = oldRaw !== null && oldRaw !== undefined ? String(oldRaw) : "";
      return oldVal === expected && actual !== expected;
    }
    case "in":
    case "not_in": {
      // Set membership over a JSON array of option tokens. Read the RAW
      // filterValue (the `expected` flattening above coerces an array to ""
      // for scalar ops — must NOT be used here), but the OPERAND still gets
      // the same two rewrites `=` applies — edge-trim and canonical ISO —
      // element by element; stored elements get the ISO fold only.
      const token = (v: unknown): string => canonicalIso(normalizeFieldValue(String(v)));
      const arr = Array.isArray(filterValue)
        ? (filterValue as unknown[]).map(token)
        : filterValue === null || filterValue === undefined
          ? []
          : [token(filterValue)];
      const member = Array.isArray(actualValue)
        ? (actualValue as unknown[])
            .map((a) => canonicalIso(String(a)))
            .some((a) => arr.includes(a))
        : arr.includes(canonicalIso(actual));
      return op === "in" ? arr.length > 0 && member : arr.length === 0 || !member;
    }
    default:
      // Note: `matches_new` (website-builder dedup op) is intentionally
      // unsupported here — same as Java. It needs the registering guest's
      // own value, which builders don't have.
      return false;
  }
}

/**
 * Resolve a key against the data object. Lookup order mirrors the server
 * evaluators exactly:
 *   1. Direct field lookup.
 *   2. Dotted path (`additionalFields.vipStatus`) — split on first dot.
 *   3. `additionalFields.*` fallback for un-prefixed keys.
 */
function resolveValue(
  key: string,
  data: Record<string, unknown> | null | undefined,
): unknown {
  if (data === null || data === undefined || key === null || key === undefined) {
    return null;
  }

  if (Object.prototype.hasOwnProperty.call(data, key)) return data[key];

  const dotIdx = key.indexOf(".");
  if (dotIdx > 0) {
    const head = key.substring(0, dotIdx);
    const tail = key.substring(dotIdx + 1);
    const nested = data[head];
    if (nested !== null && typeof nested === "object" && !Array.isArray(nested)) {
      return (nested as Record<string, unknown>)[tail] ?? null;
    }
  }

  const additionalFields = data["additionalFields"];
  if (
    additionalFields !== null &&
    typeof additionalFields === "object" &&
    !Array.isArray(additionalFields)
  ) {
    const v = (additionalFields as Record<string, unknown>)[key];
    if (v !== null && v !== undefined) return v;
  }

  return null;
}

function compareValues(a: string, b: string): number {
  if (a.length === 0 && b.length === 0) return 0;
  if (a.length === 0) return -1;
  if (b.length === 0) return 1;

  // BW30 — strict dot-decimal numeric.
  const numA = parseStrictNumber(a);
  const numB = parseStrictNumber(b);
  if (numA !== null && numB !== null) {
    if (numA < numB) return -1;
    if (numA > numB) return 1;
    return 0;
  }

  // BW31 — ISO 8601 with explicit Zulu timezone.
  const dtA = parseDateTime(a);
  const dtB = parseDateTime(b);
  if (dtA !== null && dtB !== null) {
    if (dtA < dtB) return -1;
    if (dtA > dtB) return 1;
    return 0;
  }

  return a < b ? -1 : a > b ? 1 : 0;
}

/** `^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.000Z$` — an ISO instant with explicitly zero millis. */
const ISO_ZERO_MILLIS = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.000Z$/;

/**
 * JS `Date#toISOString()` always writes millis (`.000Z`); Java
 * `Instant.toString()` omits them when zero. Same instant, two spellings —
 * fold to the Java one so `=` on a datetime field agrees across twins.
 * Applied to BOTH sides; a string that is not ISO-shaped is left alone.
 */
function canonicalIso(s: string): string {
  // Java's exact rule (ConditionEvaluator.ISO_ZERO_MILLIS): only the 24-char
  // zero-millis instant folds. A bare `/\.000Z$/` replace also folded
  // "v1.000Z" / "T.000Z" / unpadded dates, which Java leaves alone — so `=`
  // disagreed across twins on free text ending in ".000Z" (audit r1 P3/F-2).
  if (s.length !== 24 || !ISO_ZERO_MILLIS.test(s)) return s;
  return s.slice(0, 19) + "Z";
}

function parseStrictNumber(s: string): number | null {
  if (!/^-?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function parseDateTime(value: string): number | null {
  // Strip a trailing IANA zone-region suffix ("2026-06-30T15:31:00Z[UTC]") that
  // JSON-B/Yasson appends — activity-rollup dates (nextApptAt, lastEmailedAt…)
  // carry it, and without stripping the regex below fails → the FE preview
  // string-compares a rollup-date condition while the server date-compares it.
  const trimmed = value.replace(/\[[^\]]*\]$/, "");
  // Z OR explicit ±HH:MM offset — aligned with the canonical Java/TS server
  // evaluators (main-backend ConditionEvaluator, main-consumer,
  // website-builder-backend), which all accept offsets. A Z-only regex made an
  // offset datetime string-compare on the FE preview while the server
  // date-compared it (parity drift); keep all five in sync.
  if (!/T.*(Z|[+-]\d{2}:\d{2})$/.test(trimmed)) return null;
  const ms = Date.parse(trimmed);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Is this value TEXT, i.e. can "contains" mean anything about it?
 *
 * An ALLOWLIST — a new stored type defaults to "not text" and is refused rather
 * than rendered with String() and searched.
 *
 * ⚠ NOT byte-parity with Java, despite what this comment used to claim.
 *
 * Java guards `contains`/`not_contains` with TWO tests —
 * `!isTextual(value) || FilterParser.isNonTextualGuestKey(key)` — and its own
 * javadoc says the VALUE test alone is not enough: the payload builder
 * stringifies Boolean, ObjectId, Enum and Instant BEFORE evaluation, so
 * `checkedIn` arrives as the String "true" and passes any value-shaped test.
 * Java's `isTextual` also accepts Enum and Character, which this does not.
 *
 * This copy has the value test only. For a non-string BUILT-IN key
 * (`checkedIn`, `archived`, `emailOptIn`, `guestId`, `mainGuest`, …) `contains`
 * answers TRUE here and FALSE in main-backend, and `not_contains` inverts.
 * Closing it needs the key type table, which is its own change across every
 * copy at once.
 */
function isTextual(v: unknown): boolean {
  return typeof v === "string";
}
